import { MutableRefObject, RefObject, useRef, useState } from 'react';
import useIntersectionObserver, { IntersectionObserverDependencies } from '@/hooks/useIntersectionObserver';

export const useIsVisibleWithRef = <T extends Element = HTMLDivElement>(
  dependencies?: IntersectionObserverDependencies<T>,
  options?: Partial<IntersectionObserverInit> | ((ref: RefObject<T>) => Partial<IntersectionObserverInit>)
): boolean => {
  const [isInView, setIsInView] = useState(false);

  useIntersectionObserver<T>(
    (entries: IntersectionObserverEntry[]): void => {
      const result = entries.some((entry: IntersectionObserverEntry): boolean => entry.isIntersecting);
      if (result === isInView) return; // Avoid unnecessary state updates
      setIsInView(result);
    },
    dependencies,
    options
  );

  return isInView;
};

const useIsVisible = <T extends Element = HTMLDivElement>(
  dependencies?: unknown[],
  options?: Partial<IntersectionObserverInit> | ((ref: RefObject<T>) => Partial<IntersectionObserverInit>)
): [MutableRefObject<T | null>, boolean] => {
  const ref = useRef<T>(null);
  const isInView = useIsVisibleWithRef<T>([ref, ...(dependencies || [])], options);
  return [ref, isInView];
};

export default useIsVisible;
